import { useEffect, useRef } from "react";

export const RainEffect = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    // Rain drop definition
    interface Drop {
      x: number;
      y: number;
      length: number;
      speed: number;
      opacity: number;
      width: number;
    }

    const drops: Drop[] = [];
    const maxDrops = 65; // Reduced density for custom subtle ambient feel

    // Initialize drops
    for (let i = 0; i < maxDrops; i++) {
      drops.push({
        x: Math.random() * width,
        y: Math.random() * -height,
        length: Math.random() * 15 + 20, // 20px to 35px length
        speed: Math.random() * 5 + 12,    // 12px to 17px per frame
        opacity: Math.random() * 0.11 + 0.11, // Reduced ambient opacity
        width: Math.random() * 0.4 + 0.5, // Sleek, thin drops (0.5px to 0.9px)
      });
    }

    // Handle resizing
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        width = canvas.width = entry.contentRect.width || canvas.offsetWidth;
        height = canvas.height = entry.contentRect.height || canvas.offsetHeight;
      }
    });
    
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Rain color: pure white to stand out clearly on dark backgrounds
      ctx.lineCap = "round";

      for (let i = 0; i < maxDrops; i++) {
        const drop = drops[i];

        ctx.beginPath();
        ctx.strokeStyle = `rgba(255, 255, 255, ${drop.opacity})`;
        ctx.lineWidth = drop.width;
        
        // Draw slanted lines for wind-swept realistic feel (slanted left to right or right to left)
        // Let's draw slight slant (x direction shift)
        ctx.moveTo(drop.x, drop.y);
        ctx.lineTo(drop.x - 3, drop.y + drop.length);
        ctx.stroke();

        // Update positions
        drop.y += drop.speed;
        drop.x -= 1.5; // Wind effect matching slant

        // If drop goes off screen, reset to top
        if (drop.y > height) {
          drop.y = Math.random() * -50 - 20;
          drop.x = Math.random() * width;
          drop.speed = Math.random() * 5 + 12;
          drop.opacity = Math.random() * 0.11 + 0.11;
        }
        if (drop.x < 0) {
          drop.x = width;
        }
      }

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-0 mix-blend-screen opacity-90"
    />
  );
};
